import { ReactNode } from 'react';
import { IconPencil, IconTrash } from '@components/icons';
import { SIZE_S } from '@components/pill-toggle';
import { PillToggleGroup, PillToggleGroupOption } from '@components/pill-toggle-group';
import classNames from 'classnames';

import { FormFieldRowRequirement } from '../form-field-row.type';

export interface FormFieldRowDesktopProps {
  label: ReactNode;
  isCustom: boolean;
  /** When true, the editor body is open below the row — disable the edit button, render the active-state header. */
  isExpanded: boolean;
  /** Fires when the collapsed-state pencil button is clicked. */
  onEditClick: () => void;
  /** Fires when the delete button is clicked. When omitted, the delete button is not rendered. */
  onDeleteClick?: () => void;
  /** Allows the parent to know whether the row exposes a delete affordance (custom rows only). */
  hasDelete: boolean;
  requirement: FormFieldRowRequirement;
  onRequirementChange: (next: FormFieldRowRequirement) => void;
  requirementOptions: PillToggleGroupOption<FormFieldRowRequirement>[];
  disabled: boolean;
  editAriaLabel: string;
  deleteAriaLabel: string;
}

export const FormFieldRowDesktop = ({
  label,
  isCustom,
  isExpanded,
  onEditClick,
  onDeleteClick,
  hasDelete,
  requirement,
  onRequirementChange,
  requirementOptions,
  disabled,
  editAriaLabel,
  deleteAriaLabel,
}: FormFieldRowDesktopProps) => {
  const showEditButton = isCustom && !isExpanded;

  return (
    <>
      <div className='hidden min-w-0 flex-1 xs:flex'>
        {!isCustom && (
          <div className='flex min-w-0 flex-1 items-center gap-1 px-3'>
            <span className='text-body-m whitespace-nowrap text-primary'>{label}</span>
          </div>
        )}

        {showEditButton && (
          <button
            type='button'
            aria-label={editAriaLabel}
            onClick={onEditClick}
            disabled={disabled}
            data-testid='form-field-row-edit'
            className={classNames(
              'group/edit flex min-w-0 flex-1 items-center gap-1 px-3',
              'h-full text-left',
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary'
            )}>
            <span className='text-body-m whitespace-nowrap text-primary'>{label}</span>
            <span className='inline-flex h-6 w-6 shrink-0 items-center justify-center text-secondary group-hover/edit:text-primary'>
              <IconPencil className='h-4 w-4' />
            </span>
          </button>
        )}

        {isCustom && isExpanded && (
          <div className='flex min-w-0 flex-1 items-center gap-1 px-3'>
            <span className='text-body-m whitespace-nowrap text-primary'>{label}</span>
            <span
              aria-hidden
              data-testid='form-field-row-edit-active'
              className='inline-flex h-6 w-6 shrink-0 items-center justify-center text-primary'>
              <IconPencil className='h-4 w-4' />
            </span>
            {hasDelete && (
              <button
                type='button'
                aria-label={deleteAriaLabel}
                onClick={onDeleteClick}
                disabled={disabled}
                data-testid='form-field-row-delete'
                className={classNames(
                  'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded',
                  'text-critical',
                  disabled && 'cursor-not-allowed opacity-50',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                )}>
                <IconTrash className='h-3.5 w-3.5' />
              </button>
            )}
          </div>
        )}
      </div>

      <div className='hidden items-center justify-end px-3 xs:flex'>
        <PillToggleGroup<FormFieldRowRequirement>
          value={requirement}
          onChange={onRequirementChange}
          options={requirementOptions}
          size={SIZE_S}
          disabled={disabled}
          ariaLabel='Field requirement'
        />
      </div>
    </>
  );
};
